import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';
import Navbar from './components/Navbar';
import MoeFx from './components/MoeFx';
import Starfield from './components/Starfield';
import GlobeView from './pages/GlobeView';
import MessagesView from './pages/MessagesView';
import FriendsView from './pages/FriendsView';
import ProfileView from './pages/ProfileView';
import AboutView from './pages/AboutView';
import PrivacyView from './pages/PrivacyView';
import ContactView from './pages/ContactView';
import ContactMessagesView from './pages/ContactMessagesView';
import PendingActivationsView from './pages/PendingActivationsView';
import LoginView from './pages/LoginView';
import ReportsView from './pages/ReportsView';
import AdminSetup from './pages/AdminSetup';
import AdminManagement from './pages/AdminManagement';
import AdminStats from './pages/AdminStats';
import AnnouncementsView from './pages/AnnouncementsView';
import SupportView from './pages/SupportView';
import { doc, getDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';

// Drifting clouds for the Default Daylight skin
const DAYLIGHT_CLOUDS = [
  { t: '14%', l: '12%', w: 90, h: 30, d: '22s' },
  { t: '22%', r: '14%', w: 120, h: 38, d: '30s' },
  { t: '72%', l: '20%', w: 80, h: 26, d: '26s' },
  { t: '64%', r: '10%', w: 100, h: 32, d: '34s' },
];

function AppContent() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bgColor, setBgColor] = useState('#0f1423'); // Dark app shell (default)
  const [navbarColor, setNavbarColor] = useState('#001F3F'); // Ocean theme navbar
  const [bgImage, setBgImage] = useState(null); // Custom background image
  const [navbarImage, setNavbarImage] = useState(null); // Custom navbar image
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [skin, setSkin] = useState(localStorage.getItem('yapper_skin') || 'daylight');
  const [moeSparkles, setMoeSparkles] = useState(localStorage.getItem('moe_sparkles') !== 'off');

  const changeSkin = (newSkin) => {
    setSkin(newSkin);
    localStorage.setItem('yapper_skin', newSkin);
  };

  const changeMoeSparkles = (enabled) => {
    setMoeSparkles(enabled);
    localStorage.setItem('moe_sparkles', enabled ? 'on' : 'off');
  };

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const userNickname = localStorage.getItem('userNickname');

    if (userId && userNickname) {
      setIsLoggedIn(true);
      loadUserData(userId);
      loadCustomImages(userId);
      setupUnreadMessagesListener(userId);
    } else {
      setLoading(false);
    }
  }, []);

  // Load custom images from localStorage
  const loadCustomImages = (userId) => {
    const savedBgImage = localStorage.getItem(`yapper_bg_image_${userId}`);
    const savedNavbarImage = localStorage.getItem(`yapper_navbar_image_${userId}`);

    if (savedBgImage) {
      setBgImage(savedBgImage);
    }
    if (savedNavbarImage) {
      setNavbarImage(savedNavbarImage);
    }
  };

  const setupUnreadMessagesListener = (userId) => {
    // Listen for unread messages in real-time
    const messagesQuery = query(
      collection(db, 'messages'),
      where('receiverId', '==', userId),
      where('read', '==', false)
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      setHasUnreadMessages(!snapshot.empty);
    });

    return unsubscribe;
  };

  const clearUnreadNotification = () => {
    setHasUnreadMessages(false);
  };

  const loadUserData = async (userId) => {
    try {
      // Load user data directly from Firestore
      const userDoc = await getDoc(doc(db, 'users', userId));

      if (userDoc.exists()) {
        const userData = userDoc.data();

        // Only pick the fields we actually need (avoid leaking sensitive data like email)
        const userWithTheme = {
          username: userData.username || null,
          themeBackgroundColor: userData.themeBackgroundColor || '#0f1423',
          themeNavbarColor: userData.themeNavbarColor || '#001F3F',
          themeEarthColor: userData.themeEarthColor || '#2faf68',
          themeOceanColor: userData.themeOceanColor || '#145fb0',
          themeBgColor: userData.themeBgColor || null,
          themeSkin: userData.themeSkin || 'daylight',
        };

        // Cache premium status so ads can be hidden for premium members
        localStorage.setItem('isPremium', userData.isPremium ? 'true' : 'false');

        setUser(userWithTheme);
        setBgColor(userWithTheme.themeBackgroundColor);
        setNavbarColor(userWithTheme.themeNavbarColor);
        changeSkin(userWithTheme.themeSkin);
      } else {
        console.error('User document not found');
      }
    } catch (err) {
      console.error('Failed to load user:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <Routes>
        <Route path="/privacy" element={<PrivacyView />} />
        <Route path="/about" element={<AboutView />} />
        <Route path="/contact" element={<ContactView />} />
        <Route path="*" element={<LoginView />} />
      </Routes>
    );
  }

  const isDaylight = skin === 'daylight';
  const isClassicSkin = skin !== 'wired' && skin !== 'moe' && skin !== 'daylight' && skin !== 'penpal';
  // Use the starfield (Default Night) space theme only when the background is the untouched default.
  // A custom background color overrides it.
  const useStarfield = isClassicSkin && !bgImage && bgColor === '#0f1423';

  // Build background style based on image, daylight sky, starfield, or color
  const appStyle = bgImage
    ? {
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }
    : isDaylight
      ? { background: 'linear-gradient(180deg, #bfe1ff 0%, #dcefff 45%, #eef6ff 100%)' }
      : useStarfield
        ? { background: 'radial-gradient(circle at 50% 36%, #232c63 0%, #0d1330 55%, var(--neutral-950, #080b16) 100%)' }
        : { backgroundColor: bgColor };

  return (
    <div className={`App ${skin === 'wired' ? 'skin-wired' : ''} ${skin === 'moe' ? 'skin-moe' : ''} ${isDaylight ? 'skin-daylight' : ''} ${skin === 'penpal' ? 'skin-penpal' : ''}`} style={appStyle}>
      {skin === 'penpal' && (
        <>
          <div className="penpal-edge penpal-edge-top" aria-hidden="true" />
          <div className="penpal-edge penpal-edge-bot" aria-hidden="true" />
        </>
      )}
      {useStarfield && <Starfield />}
      {isDaylight && <div className="sun-glow" aria-hidden="true" />}
      {isDaylight && DAYLIGHT_CLOUDS.map((c, i) => (
        <div
          key={i}
          className="daylight-cloud"
          aria-hidden="true"
          style={{
            top: c.t,
            left: c.l,
            right: c.r,
            width: c.w,
            height: c.h,
            animation: `daylightDrift ${c.d} ease-in-out infinite alternate`,
          }}
        />
      ))}
      {skin === 'wired' && (
        <>
          <div className="crt-scan" aria-hidden="true" />
          <div className="crt-rollbar" aria-hidden="true" />
          <div className="crt-flick" aria-hidden="true" />
          <div className="crt-vignette" aria-hidden="true" />
        </>
      )}
      {skin === 'moe' && <MoeFx sparkles={moeSparkles} />}
      <Navbar user={user} navbarColor={navbarColor} navbarImage={navbarImage} hasUnreadMessages={hasUnreadMessages} clearUnreadNotification={clearUnreadNotification} skin={skin} />
      <Routes>
        <Route path="/" element={<GlobeView user={user} bgColor={bgColor} skin={skin} setUser={setUser} setBgColor={setBgColor} setNavbarColor={setNavbarColor} setBgImage={setBgImage} setNavbarImage={setNavbarImage} hasUnreadMessages={hasUnreadMessages} clearUnreadNotification={clearUnreadNotification} />} />
        <Route path="/messages" element={<MessagesView user={user} clearUnreadNotification={clearUnreadNotification} />} />
        <Route path="/friends" element={<FriendsView user={user} />} />
        <Route path="/profile" element={<ProfileView user={user} skin={skin} setSkin={changeSkin} moeSparkles={moeSparkles} setMoeSparkles={changeMoeSparkles} setUser={setUser} setBgColor={setBgColor} setNavbarColor={setNavbarColor} setBgImage={setBgImage} setNavbarImage={setNavbarImage} />} />
        <Route path="/announcements" element={<AnnouncementsView />} />
        <Route path="/support" element={<SupportView />} />
        <Route path="/about" element={<AboutView />} />
        <Route path="/privacy" element={<PrivacyView />} />
        <Route path="/contact" element={<ContactView />} />
        <Route path="/contact-messages" element={<ContactMessagesView user={user} />} />
        <Route path="/pending-activations" element={<PendingActivationsView user={user} />} />
        <Route path="/reports" element={<ReportsView user={user} />} />
        <Route path="/admin-setup" element={<AdminSetup />} />
        <Route path="/admin-management" element={<AdminManagement user={user} />} />
        <Route path="/admin-stats" element={<AdminStats />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
